import React from 'react';
import ReactDOM from 'react-dom';
import { CheckCircle, AlertTriangle, HelpCircle, Info, X, Trash2 } from 'lucide-react';

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirmation",
  description = "Êtes-vous sûr de vouloir effectuer cette action ?",
  confirmText = "Confirmer",
  cancelText = "Annuler",
  type = "info", // info, success, warning, error, danger, question
  customIcon = null,
  isLoading = false
}) => {

  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch (error) {
      console.error('Erreur dans ConfirmDialog:', error);
    }
  };

  const handleOverlayClick = (e) => {
    // Ne rien faire - dialog fermable uniquement par les boutons
  };

  const getIconAndColors = () => {
    if (customIcon) {
      return { 
        icon: customIcon, 
        color: "text-gray-700", 
        bgColor: "bg-gray-100",
        buttonColor: "#448696",
        textColor: "#ffffff"
      };
    }

    switch (type) {
      case 'success':
        return { 
          icon: <CheckCircle size={40} />, 
          color: "text-green-600", 
          bgColor: "bg-green-50",
          buttonColor: "#448696",
          textColor: "#ffffff"
        };
      case 'warning':
        return { 
          icon: <AlertTriangle size={40} />, 
          color: "text-amber-600", 
          bgColor: "bg-amber-50",
          buttonColor: "#fed774",
          textColor: "#1f2937"
        };
      case 'error':
        return { 
          icon: <X size={40} />, 
          color: "text-red-600", 
          bgColor: "bg-red-50",
          buttonColor: "#fed774",
          textColor: "#1f2937"
        };
      case 'danger':
        return { 
          icon: <Trash2 size={40} />, 
          color: "text-red-600", 
          bgColor: "bg-red-50",
          buttonColor: "#fed774",
          textColor: "#ffffff"
        };
      case 'question':
        return { 
          icon: <HelpCircle size={40} />, 
          color: "text-gray-700", 
          bgColor: "bg-gray-100",
          buttonColor: "#448696",
          textColor: "#ffffff"
        };
      default:
        return { 
          icon: <Info size={40} />, 
          color: "text-gray-700", 
          bgColor: "bg-gray-100",
          buttonColor: "#448696",
          textColor: "#ffffff"
        };
    }
  };

  const { icon, color, bgColor, buttonColor, textColor } = getIconAndColors();

  const dialogContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center"
      onClick={handleOverlayClick}
      style={{ background: 'rgba(0,0,0,0.6)', zIndex: 99998 }}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 hp-scale-in"
        onClick={(e) => e.stopPropagation()}
        style={{ zIndex: 99999 }}
      >
        {/* Icône */}
        <div className="text-center mb-6">
          <div className={`mx-auto w-20 h-20 rounded-full ${bgColor} flex items-center justify-center shadow-lg`}>
            <div className={color}>{icon}</div>
          </div>
        </div>

        {/* Titre */}
        <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
          {title}
        </h3>

        {/* Description */}
        <p className="text-sm font-medium text-gray-600 text-center mb-8">
          {description}
        </p>

        {/* Boutons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
          
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ backgroundColor: buttonColor, color: textColor }}
            onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Traitement...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialogContent, document.body);
};

export default ConfirmDialog;